import React, { useCallback, useEffect, useState } from "react";
import {
  Autocomplete,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { RemoveCircleOutline, Search } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import useFetchObjects from "../../../../api/useFetchObjects";
import useAdd from "../../../../api/useAdd";
import { useAuth } from "../../../../context";
import { useTranslation } from "react-i18next";
import ListPatientTest from "./ListPatientTest";

function debounce(func, delay) {
  let timeout;
  return function (...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), delay);
  };
}

const PatientTest = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [searchTerm, setSearchTerm] = useState("");
  const [allPatients, setAllPatients] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [patientInputValue, setPatientInputValue] = useState("");
  const [forceUpdate, setForceUpdate] = useState(0);
  const [orderData, setOrderData] = useState({
    date: new Date().toISOString().split("T")[0],
    doctor: auth?.user?.user?.id || "",
    patient: null,
    urgency: false,
    discount: 0,
    tests: [],
  });
  const [formErrors, setFormErrors] = useState({});
  const [selectedTests, setSelectedTests] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [filteredTests, setFilteredTests] = useState([]);

  const { data: tests } = useFetchObjects(
    ["laboratory-tests"],
    "laboratory-tests",
    token,
  );
  const { data: doctors, isLoading: loadingDoctors } = useFetchObjects(
    ["users"],
    "users",
    token,
  );
  const {
    data: patientsData,
    isLoading: isLoadingPatients,
  } = useFetchObjects(
    ["patients", page, pageSize, searchTerm, forceUpdate],
    `patients/?page=${page}&page_size=${pageSize}&search=${searchTerm}`,
    token,
  );
  const { handleAdd, loading, resetForm } = useAdd(
    "laboratory-test-orders",
    token,
  );

  const debouncedSetSearchTerm = useCallback(
    debounce((value) => {
      setSearchTerm(value);
    }, 300),
    [],
  );

  useEffect(() => {
    if (patientsData?.results) {
      setAllPatients((previousPatients) => {
        const mergedPatients = [...previousPatients, ...patientsData.results];
        return mergedPatients.reduce((accumulator, current) => {
          if (!accumulator.find((item) => item.id === current.id)) {
            accumulator.push(current);
          }
          return accumulator;
        }, []);
      });
      setHasMore(patientsData.next !== null);
    } else {
      setHasMore(false);
    }
  }, [patientsData]);

  useEffect(() => {
    setAllPatients([]);
    setPage(1);
    setHasMore(true);
  }, [searchTerm]);

  useEffect(() => {
    if (resetForm) {
      setOrderData({
        date: new Date().toISOString().split("T")[0],
        doctor: auth?.user?.user?.id || "",
        patient: null,
        urgency: false,
        discount: 0,
        tests: [],
      });
      setSelectedTests([]);
      setFormErrors({});
      setPatientInputValue("");
      setAllPatients([]);
      setPage(1);
      setHasMore(true);
    }
  }, [resetForm, auth?.user?.user?.id]);

  useEffect(() => {
    if (tests && searchText) {
      const filtered = tests.filter((test) =>
        test.name.toLowerCase().includes(searchText.toLowerCase()),
      );
      setFilteredTests(filtered);
    } else {
      setFilteredTests([]);
    }
  }, [searchText, tests]);

  useEffect(() => {
    if (searchTerm === "") {
      setForceUpdate((currentValue) => currentValue + 1);
    }
  }, [searchTerm]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handlePatientInputChange = (event, newInputValue) => {
    setPatientInputValue(newInputValue);
    debouncedSetSearchTerm(newInputValue);
  };

  const handlePatientChange = (event, newValue) => {
    setOrderData({ ...orderData, patient: newValue });
  };

  const handleScroll = (event) => {
    const { scrollTop, clientHeight, scrollHeight } = event.target;
    if (
      scrollHeight - scrollTop <= clientHeight + 50 &&
      hasMore &&
      !isLoadingPatients
    ) {
      setPage((currentPage) => currentPage + 1);
    }
  };

  const handleAddNewTest = (test) => {
    if (test) {
      const allTests = test.sub_tests ? [test, ...test.sub_tests] : [test];
      setSelectedTests((previousTests) => [...previousTests, ...allTests]);
      setSearchText("");
    }
  };

  const handleRemoveTest = (index) => {
    const updatedTests = [...selectedTests];
    updatedTests.splice(index, 1);
    setSelectedTests(updatedTests);
  };

  const handleChangeField = (event) => {
    const { name, value } = event.target;
    setOrderData({ ...orderData, [name]: value });
  };

  const calculateTotal = () => {
    let totalCost = 0;

    selectedTests.forEach((test) => {
      if (test.is_parent_test) {
        if (test.cost) {
          totalCost += parseFloat(test.cost);
        } else if (test.child_tests?.length) {
          test.child_tests.forEach((childTest) => {
            if (childTest.cost) {
              totalCost += parseFloat(childTest.cost);
            }
          });
        }
      } else if (test.cost) {
        totalCost += parseFloat(test.cost);
      }
    });

    const discount = parseFloat(orderData.discount) || 0;
    return Math.max(0, totalCost - discount);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    let isValid = true;
    const errors = {};

    if (!orderData.patient) {
      errors.patient = t("laboratory.patientRequired");
      isValid = false;
    }
    if (!orderData.doctor) {
      errors.doctor = t("laboratory.doctorRequired");
      isValid = false;
    }

    setFormErrors(errors);
    if (isValid) {
      handleAdd({
        ...orderData,
        patient: orderData.patient?.id,
        tests: selectedTests.map((test) => ({ test_type: test.id })),
        discount: parseFloat(orderData.discount) || 0,
      });
    }
  };

  const renderBookingTab = () => (
    <>
      <Box mb={4}>
        <Typography variant="subtitle1" fontWeight={500} mb={2}>
          {t("laboratory.patientInfo")}
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t("laboratory.orderDate")}
              name="date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={orderData.date}
              onChange={handleChangeField}
              sx={{ backgroundColor: "#fff", borderRadius: 2 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={allPatients}
              getOptionLabel={(option) =>
                `${option.firstname} ${option.lastname || ""}  Phone: ${
                  option.phone || "N/A"
                })`.trim()
              }
              inputValue={patientInputValue}
              onInputChange={handlePatientInputChange}
              onChange={handlePatientChange}
              value={orderData.patient}
              ListboxProps={{
                onScroll: handleScroll,
              }}
              loading={isLoadingPatients}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("laboratory.patient")}
                  placeholder={t(
                    "laboratory.searchPatientPlaceholder",
                    "Search Patient by name or phone",
                  )}
                  error={!!formErrors.patient}
                  helperText={formErrors.patient}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <React.Fragment>
                        {isLoadingPatients ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                  sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            {loadingDoctors ? (
              <CircularProgress size={24} />
            ) : (
              <Autocomplete
                options={doctors || []}
                getOptionLabel={(option) => `${option.username}`}
                value={
                  doctors?.find((doctor) => doctor.id === orderData.doctor) ||
                  null
                }
                onChange={(event, value) =>
                  setOrderData({
                    ...orderData,
                    doctor: value ? value.id : "",
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("laboratory.doctor")}
                    error={!!formErrors.doctor}
                    helperText={formErrors.doctor}
                    sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                  />
                )}
              />
            )}
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box mb={4}>
        <Typography variant="subtitle1" fontWeight={500} mb={2}>
          {t("laboratory.addTest")}
        </Typography>
        <Autocomplete
          value={null}
          inputValue={searchText}
          onInputChange={(event, newInputValue) =>
            setSearchText(newInputValue)
          }
          onChange={(event, value) => handleAddNewTest(value)}
          options={filteredTests}
          getOptionLabel={(option) =>
            `${option.name} (${option?.category_display?.name}) `
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("laboratory.searchTest")}
              placeholder={t("laboratory.searchTestPlaceholder")}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ backgroundColor: "#fff", borderRadius: 2 }}
            />
          )}
        />
      </Box>

      <TableContainer component={Paper} elevation={1} sx={{ mb: 4 }}>
        <Table>
          <TableHead sx={{ backgroundColor: "#eef2f5" }}>
            <TableRow>
              <TableCell>{t("laboratory.testName")}</TableCell>
              <TableCell align="center">{t("laboratory.testCost")}</TableCell>
              <TableCell align="center">{t("laboratory.action")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody sx={{ bgcolor: "#ccfcfc" }}>
            {selectedTests.map((test, index) => (
              <TableRow key={index} hover>
                <TableCell>{test.name}</TableCell>
                <TableCell align="center">{test.cost || "N/A"}</TableCell>
                <TableCell align="center">
                  <Tooltip title={t("laboratory.removeTest")} arrow>
                    <IconButton
                      color="error"
                      onClick={() => handleRemoveTest(index)}
                    >
                      <RemoveCircleOutline />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={4}
      >
        <TextField
          label={t("laboratory.discount")}
          name="discount"
          type="number"
          value={orderData.discount}
          onChange={handleChangeField}
          sx={{
            width: "30%",
            backgroundColor: "#fff",
            borderRadius: 2,
          }}
        />
        <Typography variant="h6" fontWeight={600}>
          {t("laboratory.totalAmount")}: {calculateTotal()}Ø‹
        </Typography>
      </Box>

      <Stack direction="row" justifyContent="flex-end" spacing={2}>
        <Button
          size="small"
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          sx={{
            fontWeight: 700,
            backgroundColor: "primary.main",
            "&:hover": {
              backgroundColor: "primary.dark",
            },
          }}
        >
          {loading ? t("laboratory.saving") : t("laboratory.saveOrder")}
        </Button>
      </Stack>
    </>
  );

  return (
    <Card
      sx={{
        boxShadow: "0px 20px 50px rgba(0, 0, 0, 0.1)",
        borderRadius: 4,
        backgroundColor: "#f4f6f8",
        width: "100%",
      }}
    >
      <CardContent sx={{ p: 4 }}>
        <Box display="flex" alignItems="center" mb={4}>
          <Tooltip title={t("pharmacy.goBack")} arrow>
            <IconButton
              onClick={() => navigate(-1)}
              aria-label={t("pharmacy.goBack")}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Typography variant="h5" fontWeight={600} color="primary" gutterBottom>
            {t("laboratory.addPatientTestsTitle")}
          </Typography>
        </Box>

        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label={t("laboratory.testBooking", "Test Booking")} />
          <Tab label={t("laboratory.testResults", "Test Results")} />
        </Tabs>

        {activeTab === 0 ? renderBookingTab() : <ListPatientTest />}
      </CardContent>
    </Card>
  );
};

export default PatientTest;
